import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NewLead } from "@/api/types/newLead";
import { leadHeadline } from "./leadLabel";

interface NewLeadsBannerProps {
  count: number;
  latest: NewLead | undefined;
}

// created_at of the newest lead when the banner was last closed.
export const DISMISSED_KEY = "new_leads_banner_dismissed_at";

function readDismissedAt(): string | null {
  try {
    return window.localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissedAt(value: string) {
  try {
    window.localStorage.setItem(DISMISSED_KEY, value);
  } catch {
    // Private mode / storage disabled: the close lasts until reload.
  }
}

function isNewerThan(createdAt: string, dismissedAt: string | null): boolean {
  if (!dismissedAt) return true;
  const a = Date.parse(createdAt);
  const b = Date.parse(dismissedAt);
  if (Number.isNaN(a) || Number.isNaN(b)) return createdAt !== dismissedAt;
  return a > b;
}

/**
 * LEAD-2 — the global "you have unactioned leads" banner. Sticky at the
 * very top of the authenticated app (see AppShell), visible only while at
 * least one inquiry sits in stage "new". The whole bar opens /pipeline.
 * Closing it hides it only until a lead newer than the one showing at close
 * arrives, so a fresh website submission still can't be missed; the
 * Pipeline nav badge keeps counting the whole time.
 */
export function NewLeadsBanner({ count, latest }: NewLeadsBannerProps) {
  const navigate = useNavigate();
  const [dismissedAt, setDismissedAt] = useState(readDismissedAt);

  if (count <= 0) return null;
  if (latest && !isNewerThan(latest.created_at, dismissedAt)) return null;

  const headline = leadHeadline(latest);
  const label = count === 1 ? "1 new lead" : `${count} new leads`;

  function close() {
    // No `latest` (shouldn't happen while count > 0): hide for this session.
    const stamp = latest?.created_at ?? new Date().toISOString();
    writeDismissedAt(stamp);
    setDismissedAt(stamp);
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 border-b border-brass/40 bg-gradient-to-r from-espresso via-espresso-raised to-espresso text-cream shadow-lg"
    >
      {/* brass shelf-light along the top edge — ties the bar to the rail */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brass/70 to-transparent"
      />
      <div className="relative flex items-center gap-x-2 px-2 py-1.5 sm:gap-x-3 sm:px-4">
        <button
          type="button"
          onClick={() => navigate("/pipeline")}
          className="flex min-w-0 flex-1 items-center gap-x-3 rounded-md px-2 py-1 text-left transition-colors hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass-bright"
        >
          <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
            <span className="motion-safe:animate-ping absolute inline-flex h-5 w-5 rounded-full bg-brass/60" />
            <span className="relative inline-flex h-6 w-6 items-center justify-center rounded-full bg-brass/15 ring-1 ring-brass/50">
              <svg
                className="h-3.5 w-3.5 text-brass-bright"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
                <path d="M13.7 20a2 2 0 0 1-3.4 0" />
              </svg>
            </span>
          </span>

          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-cream sm:text-base">
            {label} — {headline}
          </span>

          <span className="btn-sheen hidden shrink-0 rounded-md bg-brass px-3.5 py-1.5 text-sm font-bold text-espresso transition-colors hover:bg-brass-bright sm:inline-block">
            View leads
          </span>
        </button>

        <button
          type="button"
          onClick={close}
          aria-label="Close new leads banner"
          title="Close until another lead comes in"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-cream/70 transition-colors hover:bg-white/[0.08] hover:text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass-bright"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
